/**
 * Client hooks over the room routes (docs/ROOMS.md, docs/API.md "Rooms", lane CHATCORE1's contract) for the `/rooms`
 * UI (lane CHATWEB1). One cache entry per room (`roomKeys.feed`) holds its messages by seq (oldest first), the cursor
 * for older pages and the person's optimistic sends; the room's SSE stream (`GET /rooms/:id/stream`, resumed by
 * `Last-Event-ID` and `?after=`) merges into it. The room list keeps itself current from `GET /rooms/stream`.
 * Everything is under the session root `me`, so sign-out drops it.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query'
import {
  API_BASE,
  ApiError as ApiErrorBody,
  ROOM_MESSAGES_PER_MINUTE,
  ROOM_REFUSAL_WORDS,
  RoomListStreamEvent,
  RoomMedia as RoomMediaSchema,
  RoomStreamEvent,
  type PostRoomMessageBody,
  type Room,
  type RoomDetail,
  type RoomMe,
  type RoomMedia,
  type RoomMessage,
  type RoomPage,
  type RoomReaction,
  type RoomUser,
} from '@algoboo/schema/api'
import { buildUrl, request } from './client'
import { ApiError, errorCopy, isApiError } from './errors'
import { createSseParser, type SseFrame, type StreamStatus } from './stream'
import { attempt } from '../attempt'

export const roomKeys = {
  root: () => ['me', 'rooms'] as const,
  lists: () => ['me', 'rooms', 'list'] as const,
  list: (scope: 'mine' | 'discover', q: string) => ['me', 'rooms', 'list', scope, q] as const,
  detail: (id: string) => ['me', 'rooms', 'detail', id] as const,
  feed: (id: string) => ['me', 'rooms', 'feed', id] as const,
  members: (id: string, banned = false) => ['me', 'rooms', 'members', id, banned] as const,
  invites: (id: string) => ['me', 'rooms', 'invites', id] as const,
  mentions: (id: string, q: string) => ['me', 'rooms', 'mentions', id, q] as const,
  search: (roomId: string | null, q: string) => ['me', 'rooms', 'search', roomId, q] as const,
  invite: (code: string) => ['me', 'rooms', 'invite', code] as const,
}

const roomRefusalWords = ROOM_REFUSAL_WORDS as Readonly<Record<string, string>>

// ------------------------------------------------------------------------------------------ words

/** One refusal in a sentence: the room's own words for a `ROOM_*` code, else the web's copy. */
export function roomErrorCopy(e: unknown): string {
  if (isApiError(e)) {
    const words = roomRefusalWords[e.code]
    if (e.code === 'ROOM_SLOW_MODE' || e.code === 'RATE_LIMITED') {
      const s = e.retryAfterMs !== undefined ? Math.max(1, Math.ceil(e.retryAfterMs / 1000)) : null
      const head = e.code === 'ROOM_SLOW_MODE' ? words! : `You can send ${ROOM_MESSAGES_PER_MINUTE} messages a minute in a room.`
      return s ? `${head} You can write again in ${s} s.` : head
    }
    if (words) return words
    if (e.code === 'NOT_IMPLEMENTED' || e.status === 501) return 'Rooms are not switched on on this server yet.'
    return errorCopy(e.code).body
  }
  return 'Could not reach algo.boo. Check your connection and try again.'
}

/** "Not reported" over a zero: a count the server did not send stays words. */
export function countWords(n: number | null | undefined, one: string, many: string): string {
  if (n === null || n === undefined) return 'not reported'
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`
}

// ------------------------------------------------------------------------------------------ the room feed

/** A message on its way: shown at once, replaced by the stored one when the API answers. */
export interface PendingRoomMessage {
  localId: string
  /** The Idempotency-Key: a retry reuses it, so a lost answer never writes twice. */
  key: string
  input: PostRoomMessageBody
  state: 'sending' | 'failed'
  error?: string
  createdAt: string
}

export interface RoomFeed {
  /** Oldest first, one per seq. */
  messages: RoomMessage[]
  lastSeq: number
  /** Pass as `before` for older messages; null once the room's first message is loaded. */
  nextBefore: number | null
  pending: PendingRoomMessage[]
}

export const EMPTY_ROOM_FEED: RoomFeed = { messages: [], lastSeq: 0, nextBefore: null, pending: [] }

function sameContent(p: PendingRoomMessage, m: RoomMessage): boolean {
  return p.input.kind === m.kind && (p.input.body ?? '').trim() === m.body.trim()
}

/**
 * Merge messages (any order, duplicates allowed) into the feed, one per seq: the incoming copy wins (it is the
 * server's newer word on an edit, a pin or reactions), except that a deletion is final. A message of the viewer that
 * matches a sending optimistic row replaces it (the stream can deliver it before the POST answers).
 */
export function mergeRoomMessages(feed: RoomFeed, incoming: readonly RoomMessage[], meUserId: string | null = null): RoomFeed {
  if (incoming.length === 0) return feed
  const bySeq = new Map(feed.messages.map((m) => [m.seq, m]))
  let pending = feed.pending
  let changed = false
  for (const m of incoming) {
    const prev = bySeq.get(m.seq)
    if (!prev && meUserId && m.author.user?.id === meUserId) {
      const i = pending.findIndex((p) => p.state === 'sending' && sameContent(p, m))
      if (i >= 0) pending = pending.filter((_, j) => j !== i)
    }
    if (prev && prev.deletedAt && !m.deletedAt) continue
    if (prev === m) continue
    bySeq.set(m.seq, m)
    changed = true
  }
  if (!changed && pending === feed.pending) return feed
  const messages = [...bySeq.values()].sort((a, b) => a.seq - b.seq)
  return { ...feed, messages, pending, lastSeq: Math.max(feed.lastSeq, messages[messages.length - 1]?.seq ?? 0) }
}

/** A message's reactions now (the stream's `reactions` event or a react answer). */
export function applyReactions(feed: RoomFeed, seq: number, reactions: RoomReaction[]): RoomFeed {
  const i = feed.messages.findIndex((m) => m.seq === seq)
  if (i < 0) return feed
  const messages = feed.messages.slice()
  messages[i] = { ...messages[i]!, reactions }
  return { ...feed, messages }
}

/** Toggle the viewer's reaction locally (optimistic; the answer replaces it). */
export function toggleReactionLocally(reactions: readonly RoomReaction[], emoji: string, on: boolean): RoomReaction[] {
  const hit = reactions.find((r) => r.emoji === emoji)
  if (on) {
    if (hit?.mine) return [...reactions]
    return hit ? reactions.map((r) => (r.emoji === emoji ? { ...r, count: r.count + 1, mine: true } : r)) : [...reactions, { emoji, count: 1, mine: true }]
  }
  if (!hit?.mine) return [...reactions]
  return hit.count <= 1 ? reactions.filter((r) => r.emoji !== emoji) : reactions.map((r) => (r.emoji === emoji ? { ...r, count: r.count - 1, mine: false } : r))
}

/** Newest page, then older pages until `focusSeq` is loaded (at most `maxPages`). Pages come newest first. */
export async function loadRoomFeed(roomId: string, focusSeq: number | null, signal?: AbortSignal, maxPages = 10): Promise<Pick<RoomFeed, 'messages' | 'lastSeq' | 'nextBefore'>> {
  const sig = signal ? { signal } : {}
  const first = await request('listRoomMessages', { params: { id: roomId }, query: { limit: 50 }, ...sig })
  const items = [...first.items]
  let nextBefore = first.nextBefore
  for (let i = 0; focusSeq !== null && nextBefore !== null && i < maxPages; i++) {
    const oldest = items[items.length - 1]?.seq ?? Infinity
    if (oldest <= focusSeq) break
    const page = await request('listRoomMessages', { params: { id: roomId }, query: { before: nextBefore, limit: 100 }, ...sig })
    items.push(...page.items)
    nextBefore = page.nextBefore
  }
  return { messages: items.reverse(), lastSeq: first.lastSeq, nextBefore }
}

/** The first page(s) of a room's messages (merged into what the stream already delivered). */
export const roomFeedQuery = (qc: QueryClient, roomId: string, focusSeq: number | null, meUserId: string | null) =>
  queryOptions({
    queryKey: roomKeys.feed(roomId),
    queryFn: async ({ signal }): Promise<RoomFeed> => {
      const r = await loadRoomFeed(roomId, focusSeq, signal)
      const prev = qc.getQueryData<RoomFeed>(roomKeys.feed(roomId)) ?? EMPTY_ROOM_FEED
      return mergeRoomMessages({ ...prev, lastSeq: Math.max(prev.lastSeq, r.lastSeq), nextBefore: r.nextBefore }, r.messages, meUserId)
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

export function useRoomFeed(roomId: string | null, focusSeq: number | null, meUserId: string | null) {
  const qc = useQueryClient()
  return useQuery({ ...roomFeedQuery(qc, roomId ?? '', focusSeq, meUserId), enabled: !!roomId })
}

/** Scroll-up pagination: the page before the oldest loaded message. */
export function useLoadOlder(roomId: string) {
  const qc = useQueryClient()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const busy = useRef(false)
  const load = useCallback(async () => {
    const feed = qc.getQueryData<RoomFeed>(roomKeys.feed(roomId))
    if (!feed || feed.nextBefore === null || busy.current) return
    busy.current = true
    setLoading(true)
    setError(null)
    await attempt(
      async () => {
        const page = await request('listRoomMessages', { params: { id: roomId }, query: { before: feed.nextBefore, limit: 50 } })
        qc.setQueryData<RoomFeed>(roomKeys.feed(roomId), (f) => {
          const merged = mergeRoomMessages(f ?? EMPTY_ROOM_FEED, page.items)
          return { ...merged, nextBefore: page.nextBefore }
        })
      },
      (e) => {
        setError(roomErrorCopy(e))
      },
    )
    busy.current = false
    setLoading(false)
  }, [qc, roomId])
  return { load, loading, error }
}

// ------------------------------------------------------------------------------------------ reads

export const myRoomsQuery = (q: string) =>
  infiniteQueryOptions({
    queryKey: roomKeys.list('mine', q),
    queryFn: ({ signal, pageParam }) => request('listRooms', { signal, query: { scope: 'mine', limit: 50, ...(q ? { q } : {}), ...(pageParam ? { cursor: pageParam } : {}) } }),
    initialPageParam: null as string | null,
    getNextPageParam: (p) => p.nextCursor,
    refetchInterval: 120_000,
  })

export function useMyRooms(q: string, enabled = true) {
  return useInfiniteQuery({ ...myRoomsQuery(q), enabled })
}

export const discoverRoomsQuery = (q: string) =>
  queryOptions({
    queryKey: roomKeys.list('discover', q),
    queryFn: ({ signal }) => request('listRooms', { signal, query: { scope: 'discover', limit: 30, ...(q ? { q } : {}) } }),
  })

export function useDiscoverRooms(q: string, enabled = true) {
  return useQuery({ ...discoverRoomsQuery(q), enabled })
}

export const roomQuery = (id: string) => queryOptions({ queryKey: roomKeys.detail(id), queryFn: ({ signal }) => request('getRoom', { signal, params: { id } }) })

export function useRoom(id: string | null) {
  return useQuery({ ...roomQuery(id ?? ''), enabled: !!id })
}

export const roomMembersQuery = (id: string, banned = false) =>
  infiniteQueryOptions({
    queryKey: roomKeys.members(id, banned),
    queryFn: ({ signal, pageParam }) => request('listRoomMembers', { signal, params: { id }, query: { limit: 50, ...(banned ? { banned: 'true' as const } : {}), ...(pageParam ? { cursor: pageParam } : {}) } }),
    initialPageParam: null as string | null,
    getNextPageParam: (p) => p.nextCursor,
  })

export function useRoomMembers(id: string, enabled: boolean, banned = false) {
  return useInfiniteQuery({ ...roomMembersQuery(id, banned), enabled })
}

export function useRoomInvites(id: string, enabled: boolean) {
  return useQuery({ queryKey: roomKeys.invites(id), queryFn: ({ signal }) => request('listRoomInvites', { signal, params: { id } }), enabled })
}

export function useMentionCandidates(id: string, q: string, enabled: boolean) {
  return useQuery({
    queryKey: roomKeys.mentions(id, q),
    queryFn: ({ signal }) => request('roomMentionCandidates', { signal, params: { id }, query: { q, limit: 8 } }),
    enabled,
    staleTime: 30_000,
  })
}

export function useRoomSearch(roomId: string | null, q: string, enabled: boolean) {
  return useQuery({
    queryKey: roomKeys.search(roomId, q),
    queryFn: ({ signal }) => (roomId ? request('searchRoom', { signal, params: { id: roomId }, query: { q, limit: 20 } }) : request('searchRooms', { signal, query: { q, limit: 20 } })),
    enabled: enabled && q.trim().length > 0,
  })
}

export const invitePreviewQuery = (code: string) => queryOptions({ queryKey: roomKeys.invite(code), queryFn: ({ signal }) => request('previewRoomInvite', { signal, params: { code } }), retry: false })

export function useInvitePreview(code: string) {
  return useQuery(invitePreviewQuery(code))
}

// ------------------------------------------------------------------------------------------ cache updates

type RoomPages = InfiniteData<RoomPage, string | null>

function byActivity(a: Room, b: Room): number {
  return (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt)
}

/** Put a changed room into every cached "mine" list (insert or replace, newest activity first) and its detail. */
export function upsertRoom(qc: QueryClient, room: Room): void {
  for (const [key, data] of qc.getQueriesData<RoomPages>({ queryKey: roomKeys.lists() })) {
    if (!data?.pages?.length || (key as readonly unknown[])[3] !== 'mine') continue
    const q = String((key as readonly unknown[])[4] ?? '').toLowerCase()
    const listed = data.pages.some((p) => p.items.some((r) => r.id === room.id))
    if (!listed && q && !room.name.toLowerCase().startsWith(q) && !(room.slug ?? '').startsWith(q)) continue
    const all = data.pages.flatMap((p) => p.items).filter((r) => r.id !== room.id)
    if (!room.archivedAt) all.push(room)
    all.sort(byActivity)
    // the list is small and current: every loaded room goes on the first page in activity order (rows dedupe by id)
    const [first, ...rest] = data.pages
    qc.setQueryData<RoomPages>(key, { ...data, pages: [{ ...first!, items: all }, ...rest.map((p) => ({ ...p, items: [] }))] })
  }
  qc.setQueryData<RoomDetail>(roomKeys.detail(room.id), (d) => (d ? { ...d, ...room } : d))
}

export function removeRoom(qc: QueryClient, roomId: string): void {
  for (const [key, data] of qc.getQueriesData<RoomPages>({ queryKey: roomKeys.lists() })) {
    if (!data?.pages?.length || (key as readonly unknown[])[3] !== 'mine') continue
    qc.setQueryData<RoomPages>(key, { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.filter((r) => r.id !== roomId) })) })
  }
}

/** The viewer's membership changed (read state, mute): every cached copy of the room shows it. */
export function patchRoomMe(qc: QueryClient, roomId: string, patch: Partial<RoomMe>): void {
  const apply = (r: Room): Room => (r.id === roomId && r.me ? { ...r, me: { ...r.me, ...patch } } : r)
  for (const [key, data] of qc.getQueriesData<RoomPages>({ queryKey: roomKeys.lists() })) {
    if (!data?.pages?.length || (key as readonly unknown[])[3] !== 'mine') continue
    qc.setQueryData<RoomPages>(key, { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.map(apply) })) })
  }
  qc.setQueryData<RoomDetail>(roomKeys.detail(roomId), (d) => (d ? (apply(d) as RoomDetail) : d))
}

// ------------------------------------------------------------------------------------------ streams

export interface SseOptions {
  url: (after: number) => string
  /** The highest seq held (read at every (re)connect); 0 sends no `Last-Event-ID`. */
  after?: () => number
  onFrames: (frames: SseFrame[]) => void
  onStatus: (s: StreamStatus) => void
  /** After a reconnect: reload what the stream does not replay. */
  onResync?: () => void
  fetch?: typeof fetch
  delay?: (attempt: number) => number
}

/**
 * Keep an SSE stream open over fetch (so `Last-Event-ID` goes out on every reconnect): a closed or failed
 * connection reconnects with backoff; a 4xx answer (signed out, not a member, not deployed) stops it for good.
 * Returns the close function.
 */
export function openSse(o: SseOptions): () => void {
  const controller = new AbortController()
  const fetchImpl = o.fetch ?? globalThis.fetch
  const delay = o.delay ?? ((n: number) => Math.min(30_000, 1_000 * 2 ** Math.min(n - 1, 5)))
  let attempt = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let lastId = 0
  let shown: StreamStatus | null = null
  const status = (s: StreamStatus) => {
    if (s !== shown) o.onStatus((shown = s))
  }
  const connect = async () => {
    const resumed = attempt > 0
    status(resumed ? 'reconnecting' : 'connecting')
    try {
      const after = Math.max(o.after?.() ?? 0, lastId)
      const headers: Record<string, string> = { Accept: 'text/event-stream' }
      if (o.after && after > 0) headers['Last-Event-ID'] = String(after)
      const res = await fetchImpl(o.url(after), { headers, credentials: 'include', signal: controller.signal })
      if ((res.status >= 400 && res.status < 500) || res.status === 501) {
        status('off')
        return
      }
      if (!res.ok || !res.body) throw new Error(`stream http ${res.status}`)
      status('live')
      attempt = 0
      if (resumed) o.onResync?.()
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      const parse = createSseParser()
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        const frames = parse(decoder.decode(value, { stream: true }))
        for (const f of frames) if (f.id !== undefined && /^\d+$/.test(f.id)) lastId = Math.max(lastId, Number(f.id))
        if (frames.length) o.onFrames(frames)
        if (controller.signal.aborted) return
      }
      throw new Error('stream closed')
    } catch {
      if (controller.signal.aborted) return
      attempt++
      status('reconnecting')
      timer = setTimeout(() => void connect(), delay(attempt))
    }
  }
  void connect()
  return () => {
    controller.abort()
    if (timer) clearTimeout(timer)
  }
}

/** Frames in one read above which typing and presence are dropped (messages never are). */
export const EPHEMERAL_BACKLOG = 32

/**
 * Parse a batch of room-stream frames. Under backpressure (a batch longer than EPHEMERAL_BACKLOG) the ephemeral
 * events, typing and presence, are dropped; every other event is kept, in order.
 */
export function roomEventsOf(frames: readonly SseFrame[]): RoomStreamEvent[] {
  const pressed = frames.length > EPHEMERAL_BACKLOG
  const out: RoomStreamEvent[] = []
  for (const f of frames) {
    if (pressed && (f.event === 'typing' || f.event === 'presence')) continue
    let json: unknown
    try {
      json = JSON.parse(f.data)
    } catch {
      continue
    }
    const parsed = RoomStreamEvent.safeParse(json)
    if (!parsed.success) {
      console.warn('[room-stream] dropped an event that failed validation', f.event)
      continue
    }
    if (pressed && (parsed.data.type === 'typing' || parsed.data.type === 'presence')) continue
    out.push(parsed.data)
  }
  return out
}

export interface RoomLive {
  status: StreamStatus
  /** People typing now (never the viewer). */
  typing: RoomUser[]
  /** People with the room open now. */
  present: RoomUser[]
  /** Set when the server ended the viewer's stream (left, removed, banned, archived). */
  closed: 'left' | 'kicked' | 'banned' | 'archived' | null
}

/** Keep the room's feed and detail live while it is open; typing and presence live here, never in the cache. */
export function useRoomStream(roomId: string | null, enabled: boolean, meUserId: string | null): RoomLive {
  const qc = useQueryClient()
  const [status, setStatus] = useState<StreamStatus>('off')
  const [closed, setClosed] = useState<RoomLive['closed']>(null)
  const [typing, setTyping] = useState<Map<string, { user: RoomUser; until: number }>>(() => new Map())
  const [present, setPresent] = useState<Map<string, { user: RoomUser; until: number }>>(() => new Map())

  // a new room (or the stream turned back on) starts with no `closed` from the last one
  const [following, setFollowing] = useState(`${roomId}|${enabled}`)
  if (following !== `${roomId}|${enabled}`) {
    setFollowing(`${roomId}|${enabled}`)
    setClosed(null)
  }
  useEffect(() => {
    if (!roomId || !enabled) return
    const key = roomKeys.feed(roomId)
    let stop: () => void = () => {}
    const onEvent = (e: RoomStreamEvent) => {
      const now = Date.now()
      switch (e.type) {
        case 'message':
          qc.setQueryData<RoomFeed>(key, (f) => mergeRoomMessages(f ?? EMPTY_ROOM_FEED, [e.message], meUserId))
          // a writer's message ends their typing
          if (e.message.author.user)
            setTyping((m) => {
              if (!m.has(e.message.author.user!.id)) return m
              const n = new Map(m)
              n.delete(e.message.author.user!.id)
              return n
            })
          break
        case 'message_updated':
          qc.setQueryData<RoomFeed>(key, (f) => mergeRoomMessages(f ?? EMPTY_ROOM_FEED, [e.message], meUserId))
          void qc.invalidateQueries({ queryKey: roomKeys.detail(roomId) })
          break
        case 'reactions':
          qc.setQueryData<RoomFeed>(key, (f) => applyReactions(f ?? EMPTY_ROOM_FEED, e.seq, e.reactions))
          break
        case 'typing':
          if (e.user.id === meUserId) break
          setTyping((m) => new Map(m).set(e.user.id, { user: e.user, until: now + e.ttlMs }))
          break
        case 'presence':
          setPresent((m) => {
            const n = new Map(m)
            for (const u of e.users) n.set(u.id, { user: u, until: now + e.ttlMs })
            return n
          })
          break
        case 'member':
          void qc.invalidateQueries({ queryKey: ['me', 'rooms', 'members', roomId] })
          break
        case 'room':
          upsertRoom(qc, e.room)
          break
        case 'closed':
          setClosed(e.reason)
          void qc.invalidateQueries({ queryKey: roomKeys.detail(roomId) })
          stop()
          break
      }
    }
    stop = openSse({
      url: (after) => buildUrl('streamRoom', { params: { id: roomId }, query: after > 0 ? { after } : {} }),
      after: () => qc.getQueryData<RoomFeed>(key)?.lastSeq ?? 0,
      onStatus: setStatus,
      onFrames: (frames) => {
        for (const e of roomEventsOf(frames)) onEvent(e)
      },
      onResync: () => void qc.invalidateQueries({ queryKey: roomKeys.detail(roomId) }),
    })
    const prune = setInterval(() => {
      const now = Date.now()
      const drop = (m: Map<string, { user: RoomUser; until: number }>) => {
        let changed = false
        const n = new Map(m)
        for (const [k, v] of n) if (v.until <= now) changed = n.delete(k) || changed
        return changed ? n : m
      }
      setTyping(drop)
      setPresent(drop)
    }, 1_000)
    return () => {
      stop()
      clearInterval(prune)
      setTyping(new Map())
      setPresent(new Map())
    }
  }, [roomId, enabled, qc, meUserId])

  return { status: roomId && enabled ? status : 'off', typing: [...typing.values()].map((v) => v.user), present: [...present.values()].map((v) => v.user), closed }
}

/** Keep the room list current: a changed room moves to the top with its unread counts; a reconnect reloads. */
export function useRoomListStream(enabled: boolean): StreamStatus {
  const qc = useQueryClient()
  const [status, setStatus] = useState<StreamStatus>('off')
  useEffect(() => {
    if (!enabled) return
    return openSse({
      url: () => `${API_BASE}/rooms/stream`,
      onStatus: setStatus,
      onFrames: (frames) => {
        for (const f of frames) {
          let json: unknown
          try {
            json = JSON.parse(f.data)
          } catch {
            continue
          }
          const parsed = RoomListStreamEvent.safeParse(json)
          if (!parsed.success) continue
          if (parsed.data.type === 'room') upsertRoom(qc, parsed.data.room)
          else removeRoom(qc, parsed.data.roomId)
        }
      },
      onResync: () => void qc.invalidateQueries({ queryKey: roomKeys.lists() }),
    })
  }, [enabled, qc])
  return enabled ? status : 'off'
}

// ------------------------------------------------------------------------------------------ writes

function newKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

const sentAt = new Map<string, number[]>()
const slowUntil = new Map<string, number>()

/** Test hook: forget local send times and slow-mode waits. */
export function resetRoomSendGuards(): void {
  sentAt.clear()
  slowUntil.clear()
}

/** When the viewer may write in the room again (ms epoch), from slow mode and the last refusal; 0 = now. */
export function roomWriteAt(roomId: string): number {
  return slowUntil.get(roomId) ?? 0
}

/**
 * Client-side guards before a send: the room's 60 a minute, and slow mode for members (owner and admins are exempt).
 * Null when the message may go now, else the sentence.
 */
export function roomSendGuard(room: Pick<Room, 'id' | 'settings' | 'me'>, now = Date.now()): string | null {
  const wait = (slowUntil.get(room.id) ?? 0) - now
  if (wait > 0) return `${roomRefusalWords['ROOM_SLOW_MODE']} You can write again in ${Math.ceil(wait / 1000)} s.`
  const list = (sentAt.get(room.id) ?? []).filter((t) => now - t < 60_000)
  sentAt.set(room.id, list)
  if (list.length >= ROOM_MESSAGES_PER_MINUTE) return `You can send ${ROOM_MESSAGES_PER_MINUTE} messages a minute in a room. You can write again in ${Math.ceil((60_000 - (now - list[0]!)) / 1000)} s.`
  return null
}

function recordRoomSend(room: Pick<Room, 'id' | 'settings' | 'me'>, now = Date.now()): void {
  sentAt.set(room.id, [...(sentAt.get(room.id) ?? []), now])
  const exempt = room.me?.role === 'owner' || room.me?.role === 'admin'
  if (!exempt && room.settings.slowModeS > 0) slowUntil.set(room.id, now + room.settings.slowModeS * 1000)
}

/** Post the viewer's messages: optimistic, idempotent (one key per message, reused on retry). */
export function usePostRoomMessage(room: Pick<Room, 'id' | 'settings' | 'me'>, meUserId: string | null) {
  const qc = useQueryClient()
  const roomRef = useRef(room)
  useLayoutEffect(() => {
    roomRef.current = room
  })
  const roomId = room.id
  const run = useCallback(
    async (p: PendingRoomMessage) => {
      const key = roomKeys.feed(roomId)
      const set = (fn: (f: RoomFeed) => RoomFeed) => qc.setQueryData<RoomFeed>(key, (f) => fn(f ?? EMPTY_ROOM_FEED))
      const { error: _drop, ...rest } = p
      set((f) => ({ ...f, pending: [...f.pending.filter((x) => x.localId !== p.localId), { ...rest, state: 'sending' }] }))
      await attempt(
        async () => {
          const m = await request('postRoomMessage', { params: { id: roomId }, body: p.input, idempotencyKey: p.key })
          set((f) => {
            const merged = mergeRoomMessages(f, [m], meUserId)
            return { ...merged, pending: merged.pending.filter((x) => x.localId !== p.localId) }
          })
          patchRoomMe(qc, roomId, { lastReadSeq: m.seq, unread: 0, unreadMentions: 0 })
        },
        (e) => {
          if (isApiError(e) && e.code === 'ROOM_SLOW_MODE' && e.retryAfterMs !== undefined) slowUntil.set(roomId, Date.now() + e.retryAfterMs)
          set((f) => ({ ...f, pending: f.pending.map((x) => (x.localId === p.localId ? { ...x, state: 'failed', error: roomErrorCopy(e) } : x)) }))
        },
      )
    },
    [qc, roomId, meUserId],
  )
  const send = useCallback(
    (input: PostRoomMessageBody): string | null => {
      const refused = roomSendGuard(roomRef.current)
      if (refused) return refused
      recordRoomSend(roomRef.current)
      const id = newKey()
      void run({ localId: id, key: id, input, state: 'sending', createdAt: new Date().toISOString() })
      return null
    },
    [run],
  )
  const retry = useCallback((p: PendingRoomMessage) => void run(p), [run])
  const discard = useCallback((localId: string) => qc.setQueryData<RoomFeed>(roomKeys.feed(roomId), (f) => (f ? { ...f, pending: f.pending.filter((x) => x.localId !== localId) } : f)), [qc, roomId])
  return { send, retry, discard }
}

/** Mark the room read up to `seq` once it was shown (debounced; never backwards). */
export function useMarkRoomRead(room: Pick<Room, 'id' | 'me'> | null, seq: number) {
  const qc = useQueryClient()
  const sent = useRef(0)
  const roomId = room?.id ?? null
  const read = room?.me?.lastReadSeq ?? null
  useEffect(() => {
    if (!roomId || read === null || seq <= read || seq <= sent.current) return
    const t = setTimeout(() => {
      sent.current = seq
      patchRoomMe(qc, roomId, { lastReadSeq: seq, unread: 0, unreadMentions: 0 })
      void request('markRoomRead', { params: { id: roomId }, body: { lastReadSeq: seq }, idempotencyKey: newKey() })
        .then((r) => patchRoomMe(qc, roomId, { lastReadSeq: r.lastReadSeq, unread: r.unread, unreadMentions: r.unreadMentions }))
        .catch(() => {
          sent.current = 0
        })
    }, 600)
    return () => clearTimeout(t)
  }, [qc, roomId, read, seq])
}

/** Tell the room the viewer types: at most once every 4 s while they keep typing. */
export function useTypingSignal(roomId: string, enabled: boolean) {
  const last = useRef(0)
  return useCallback(() => {
    if (!enabled) return
    const now = Date.now()
    if (now - last.current < 4_000) return
    last.current = now
    void request('roomTyping', { params: { id: roomId }, body: {} }).catch(() => undefined)
  }, [roomId, enabled])
}

/** React or take a reaction back: optimistic, the answer is the message's reactions now. */
export function useToggleRoomReaction(roomId: string) {
  const qc = useQueryClient()
  return useCallback(
    async (seq: number, emoji: string, on: boolean): Promise<string | null> => {
      const key = roomKeys.feed(roomId)
      const before = qc.getQueryData<RoomFeed>(key)?.messages.find((m) => m.seq === seq)?.reactions ?? []
      qc.setQueryData<RoomFeed>(key, (f) => applyReactions(f ?? EMPTY_ROOM_FEED, seq, toggleReactionLocally(before, emoji, on)))
      return request(on ? 'reactRoomMessage' : 'unreactRoomMessage', { params: { id: roomId, seq }, body: { emoji }, idempotencyKey: newKey() })
        .then((r) => {
          qc.setQueryData<RoomFeed>(key, (f) => applyReactions(f ?? EMPTY_ROOM_FEED, r.seq, r.reactions))
          return null
        })
        .catch((e: unknown) => {
          qc.setQueryData<RoomFeed>(key, (f) => applyReactions(f ?? EMPTY_ROOM_FEED, seq, before))
          return roomErrorCopy(e)
        })
    },
    [qc, roomId],
  )
}

/** Put one changed message (edit, delete, pin) into the feed. */
export function putRoomMessage(qc: QueryClient, m: RoomMessage): void {
  qc.setQueryData<RoomFeed>(roomKeys.feed(m.roomId), (f) => mergeRoomMessages(f ?? EMPTY_ROOM_FEED, [m]))
}

/**
 * Upload one image for a `media` message (`POST /rooms/:id/media`, multipart field `file`). The typed client sends
 * JSON only, so this one route goes through fetch with the same credentials, key and fail-closed answer check.
 */
export async function uploadRoomMedia(roomId: string, file: File, opts: { fetch?: typeof fetch; signal?: AbortSignal } = {}): Promise<RoomMedia> {
  const form = new FormData()
  form.set('file', file)
  const fetchImpl = opts.fetch ?? globalThis.fetch
  let res: Response
  try {
    res = await fetchImpl(buildUrl('uploadRoomMedia', { params: { id: roomId } }), {
      method: 'POST',
      body: form,
      credentials: 'include',
      headers: { Accept: 'application/json', 'Idempotency-Key': newKey() },
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
  } catch (e) {
    throw new ApiError({ code: 'NETWORK', status: 0, route: 'uploadRoomMedia', details: String(e) })
  }
  let json: unknown = undefined
  try {
    json = await res.json()
  } catch {
    // an empty or non-JSON answer is judged below
  }
  if (!res.ok) {
    const err = ApiErrorBody.safeParse(json)
    if (err.success) throw new ApiError({ code: err.data.error.code, status: res.status, message: err.data.error.message, route: 'uploadRoomMedia' })
    throw new ApiError({ code: 'HTTP', status: res.status, route: 'uploadRoomMedia' })
  }
  const ok = RoomMediaSchema.safeParse(json)
  if (!ok.success) throw new ApiError({ code: 'INVALID_RESPONSE', status: res.status, route: 'uploadRoomMedia', details: ok.error.issues })
  return ok.data
}

export { newKey as newRoomKey }
